import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {get, ref, serverTimestamp, set} from 'firebase/database';
import {database} from '../../services/firebase';
import {useAuth} from '../../contexts/AuthContext';
import Header from '../../components/common/Header';
import {biblicalBoooksAndCategories} from "../../utils/utils";

const {categories, biblicalBooks} = biblicalBoooksAndCategories();

export default function TrainingPage() {
    const {currentUser} = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [userConfig, setUserConfig] = useState(null);

    // Options de configuration
    const [numQuestions, setNumQuestions] = useState(10);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedBooks, setSelectedBooks] = useState([]);
    const [difficulty, setDifficulty] = useState('mixed');

    useEffect(() => {
        async function fetchUserConfig() {
            if (!currentUser) return;

            try {
                // Récupérer la configuration d'entraînement de l'utilisateur
                const configRef = ref(database, `trainingConfigs/${currentUser.uid}`);
                const configSnapshot = await get(configRef);

                if (configSnapshot.exists()) {
                    const config = configSnapshot.val();
                    setUserConfig(config);
                    setNumQuestions(config.defaultNumQuestions || 10);
                    setSelectedCategories(config.preferredCategories || []);
                    setSelectedBooks(config.preferredBooks || []);
                    setDifficulty(config.preferredDifficulty || 'mixed');
                }

                setLoading(false);
            } catch (error) {
                console.error("Erreur lors de la récupération de la configuration:", error);
                setLoading(false);
            }
        }

        fetchUserConfig();
    }, [currentUser]);

    const handleCategoryToggle = (categoryId) => {
        setSelectedCategories(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    const handleBookToggle = (bookId) => {
        setSelectedBooks(prev =>
            prev.includes(bookId)
                ? prev.filter(id => id !== bookId)
                : [...prev, bookId]
        );
    };

    const handleTestamentFilter = (testament) => {
        const testamentBooks = biblicalBooks
            .filter(book => book.testament === testament)
            .map(book => book.id);

        // Si tous les livres du testament sont déjà sélectionnés, les désélectionner
        const allSelected = testamentBooks.every(bookId => selectedBooks.includes(bookId));

        if (allSelected) {
            setSelectedBooks(prev => prev.filter(id => !testamentBooks.includes(id)));
        } else {
            // Sinon, ajouter tous les livres non sélectionnés
            setSelectedBooks(prev => {
                const currentSet = new Set(prev);
                testamentBooks.forEach(bookId => currentSet.add(bookId));
                return Array.from(currentSet);
            });
        }
    };

    const handleSaveConfig = async () => {
        if (!currentUser) return;

        try {
            const configRef = ref(database, `trainingConfigs/${currentUser.uid}`);

            await set(configRef, {
                defaultNumQuestions: numQuestions,
                preferredCategories: selectedCategories,
                preferredBooks: selectedBooks,
                preferredDifficulty: difficulty,
                excludedQuestions: userConfig?.excludedQuestions || [],
                updatedAt: serverTimestamp()
            });

            alert("Configuration enregistrée avec succès !");
        } catch (error) {
            console.error("Erreur lors de l'enregistrement de la configuration:", error);
            alert("Erreur lors de l'enregistrement. Veuillez réessayer.");
        }
    };

    const handleStartTraining = () => {
        // Enregistrer temporairement la configuration actuelle
        const trainingConfig = {
            numQuestions,
            categories: selectedCategories,
            books: selectedBooks,
            difficulty
        };

        // Stocker dans sessionStorage pour la récupérer sur la page de quiz
        sessionStorage.setItem('trainingConfig', JSON.stringify(trainingConfig));

        // Naviguer vers la page de quiz en mode entraînement
        navigate('/training/session');
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-b from-indigo-50 to-purple-100 min-h-screen pb-12">
            <Header title="Mode Entraînement" showBackButton={true}/>

            <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                    <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-indigo-500 to-purple-600">
                        <h2 className="text-lg leading-6 font-medium text-white">
                            Personnalisez votre session d'entraînement
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-indigo-100">
                            Configurez les paramètres ci-dessous pour créer votre quiz personnalisé.
                        </p>
                    </div>

                    <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                                <label htmlFor="num-questions" className="block text-sm font-medium text-gray-700">
                                    Nombre de questions
                                </label>
                                <div className="mt-1">
                                    <select
                                        id="num-questions"
                                        name="num-questions"
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        value={numQuestions}
                                        onChange={(e) => setNumQuestions(Number(e.target.value))}
                                    >
                                        <option value={5}>5 questions</option>
                                        <option value={10}>10 questions</option>
                                        <option value={15}>15 questions</option>
                                        <option value={20}>20 questions</option>
                                        <option value={30}>30 questions</option>
                                        <option value={50}>50 questions</option>
                                    </select>
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700">
                                    Niveau de difficulté
                                </label>
                                <div className="mt-1">
                                    <select
                                        id="difficulty"
                                        name="difficulty"
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        value={difficulty}
                                        onChange={(e) => setDifficulty(e.target.value)}
                                    >
                                        <option value="easy">Facile</option>
                                        <option value="medium">Moyen</option>
                                        <option value="hard">Difficile</option>
                                        <option value="mixed">Mixte</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6">
                            <h3 className="text-lg font-medium text-gray-900">Catégories</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Sélectionnez les catégories de questions à inclure dans votre quiz.
                            </p>
                            <div
                                className="mt-4 grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 md:grid-cols-3 lg:grid-cols-4">
                                {categories.map((category) => (
                                    <div key={category.id} className="relative flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id={`category-${category.id}`}
                                                name={`category-${category.id}`}
                                                type="checkbox"
                                                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                checked={selectedCategories.includes(category.id)}
                                                onChange={() => handleCategoryToggle(category.id)}
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label htmlFor={`category-${category.id}`}
                                                   className="font-medium text-gray-700">
                                                {category.name}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-6">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-medium text-gray-900">Livres bibliques</h3>
                                <div className="flex space-x-4">
                                    <button
                                        type="button"
                                        onClick={() => handleTestamentFilter('old')}
                                        className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        Ancien Testament
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleTestamentFilter('new')}
                                        className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        Nouveau Testament
                                    </button>
                                </div>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                                Sélectionnez les livres de la Bible à inclure dans votre quiz.
                            </p>
                            <div
                                className="mt-4 grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 md:grid-cols-3 lg:grid-cols-4">
                                {biblicalBooks.map((book) => (
                                    <div key={book.id} className="relative flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id={`book-${book.id}`}
                                                name={`book-${book.id}`}
                                                type="checkbox"
                                                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                checked={selectedBooks.includes(book.id)}
                                                onChange={() => handleBookToggle(book.id)}
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label htmlFor={`book-${book.id}`} className="font-medium text-gray-700">
                                                {book.name}
                                            </label>
                                            <p className="text-gray-500">
                                                {book.testament === 'old' ? 'A.T.' : 'N.T.'}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-8 flex justify-between">
                            <button
                                type="button"
                                onClick={handleSaveConfig}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Enregistrer comme configuration par défaut
                            </button>
                            <button
                                type="button"
                                onClick={handleStartTraining}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Commencer l'entraînement
                            </button>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
